import React, { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The 5 moods align with Rowdy's supportive vocabulary — not "happy/sad" but
// where you *actually* land after a practice.
const MOODS = [
  { id: 'peaceful',   label: 'Peaceful',        emoji: '🕊️' },
  { id: 'hopeful',    label: 'Hopeful',         emoji: '✨' },
  { id: 'stronger',   label: 'Stronger',        emoji: '💪' },
  { id: 'processing', label: 'Still Processing','emoji': '🌊' },
  { id: 'grateful',   label: 'Grateful',        emoji: '🌻' },
];

/**
 * CheckInModal — shown after a meditation / breathing / affirmation
 * completes. Small, quick, closes the reflection loop.
 *
 * Props
 *   open, onClose
 *   ref — content ref that triggered the check-in (breath:box-breath, post:xyz…)
 *   title — heading override; defaults to "How are you feeling now?"
 */
const CheckInModal = ({ open, onClose, contentRef, title = 'How are you feeling now?' }) => {
  const [mood, setMood]       = useState(null);
  const [share, setShare]     = useState(false);
  const [saving, setSaving]   = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!mood) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/community/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mood, ref: contentRef || null, share }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Could not save — try again');
      } else {
        toast.success(share ? 'Shared with the community 🌱' : 'Saved 💛');
        onClose?.();
        setMood(null);
        setShare(false);
      }
    } catch {
      toast.error('Network error — try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-3 pb-3"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="checkin-modal"
    >
      <div className="bg-[#FFFCF3] w-full max-w-[380px] rounded-3xl shadow-2xl border border-[#D6AD60]/40 p-5 animate-scale-in">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute -mt-3 -mr-2 top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-800"
        >
          <X className="w-4 h-4" />
        </button>

        <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold mb-1" style={{ color: '#D6AD60' }}>
          <Sparkles className="w-3 h-3" /> Check-In
        </p>
        <h3
          className="text-lg text-[#173F35] mb-4 pr-8"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {title}
        </h3>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {MOODS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMood(m.id)}
              data-testid={`checkin-mood-${m.id}`}
              className={`flex flex-col items-center gap-1 py-3 rounded-2xl border transition-all active:scale-95 ${mood === m.id ? 'border-[#D6AD60] bg-[#D6AD60]/15' : 'border-stone-200 bg-white hover:bg-stone-50'}`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <span className="text-2xl">{m.emoji}</span>
              <span className="text-[11px] font-medium text-stone-700">{m.label}</span>
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-[12px] text-stone-600 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={share}
            onChange={(e) => setShare(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-stone-300 accent-[#D6AD60]"
            data-testid="checkin-share-toggle"
          />
          Share with the community
        </label>

        <button
          type="button"
          onClick={submit}
          disabled={!mood || saving}
          data-testid="checkin-submit"
          className="w-full py-3 rbr-btn-gold text-[13px] disabled:opacity-60"
        >
          {saving ? 'Saving…' : mood ? 'Save my check-in' : 'Pick a mood'}
        </button>
      </div>
    </div>
  );
};

export default CheckInModal;
